import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SlideCarousel } from "@/components/SlideCarousel";
import { StateIcon, StateLegend } from "@/components/SessionStateIcons";
import { formatSessionRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, CalendarClock, Loader2, User } from "lucide-react";

const daysUntil = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000));
const nextLabel = (d) => (d === 0 ? "La próxima sesión inicia hoy" : `La próxima sesión inicia en ${d} día${d === 1 ? "" : "s"}`);

function SessionsSlide({ t }) {
  const next = t.sessions.find((s) => new Date(s.starts_at) > new Date());
  return (
    <div className="rounded-3xl overflow-hidden border bg-card shadow-[0_14px_40px_-16px_rgba(121,68,228,0.45)]" data-testid={`my-sessions-workshop-${t.id}`}>
      <div className="bg-primary text-primary-foreground p-5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-[0.2em] opacity-80">VIVOTaller · Mis sesiones</p>
          {t.kind === "canje" && <Badge className="bg-white text-primary hover:bg-white">Canje</Badge>}
        </div>
        <h3 className="font-display font-extrabold text-xl mt-2 leading-tight break-words">{t.workshop_name}</h3>
        <p className="text-sm opacity-90 mt-1 flex items-center gap-1.5"><User className="w-3.5 h-3.5" />Maestro: <strong>{t.business?.name || "—"}</strong></p>
        {next && <p className="text-sm mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 font-medium" data-testid={`next-session-days-${t.id}`}><CalendarClock className="w-3.5 h-3.5" />{nextLabel(daysUntil(next.starts_at))}</p>}
      </div>
      <div className="relative flex items-center">
        <div className="w-5 h-5 rounded-full bg-background -ml-2.5 border-r" />
        <div className="flex-1 border-t-2 border-dashed border-border" />
        <div className="w-5 h-5 rounded-full bg-background -mr-2.5 border-l" />
      </div>
      <ul className="p-5 space-y-2">
        {t.sessions.map((s, i) => {
          const isNext = next?.id === s.id;
          return (
          <li key={s.id} className={`flex items-center gap-2.5 text-sm rounded-xl px-2 py-1 -mx-2 ${isNext ? "bg-primary/10 text-primary font-semibold" : ""}`} data-testid={`my-session-${s.id}`} data-next={isNext || undefined}>
            <StateIcon state={s.state} testId={`my-session-state-${s.id}`} />
            <span className={`w-5 ${isNext ? "" : "text-muted-foreground"}`}>{i + 1}.</span>
            <span data-testid={`my-session-range-${s.id}`}>{formatSessionRange(s.starts_at, t.duration_min || 0)}</span>
          </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function MySessionsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get("/reservations/mine").then((r) => {
      setItems([...(r.data.active || []), ...(r.data.history || [])].filter((t) => t.sessions?.length));
    }).catch(() => setItems([]));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="sessions-login-required">
        <CalendarDays className="w-10 h-10 mx-auto mb-3 text-primary" />
        <h1 className="text-2xl font-extrabold">Mis sesiones</h1>
        <Button asChild className="mt-6"><Link to="/login?next=/mis-sesiones">Iniciar sesión</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="my-sessions">
      <h1 className="text-3xl font-extrabold tracking-tight">Mis sesiones</h1>
      <p className="text-muted-foreground mt-1">Consulta las sesiones de tus talleres activos o por comenzar. Pasa el cursor o toca los íconos para ver su significado.</p>
      <div className="mt-4"><StateLegend /></div>
      {!items ? <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mt-16" /> : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="my-sessions-empty">
          <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-40" />Aún no tienes talleres reservados.
          <div><Button asChild variant="link"><Link to="/explorar">Explorar talleres</Link></Button></div>
        </div>
      ) : (
        <div className="mt-8">
          <SlideCarousel items={items} prefix="sessions" label="Taller" render={(t) => <SessionsSlide t={t} />} />
        </div>
      )}
    </div>
  );
}
